import * as React from "react";
import {
  HTMLTable,
  HTMLSelect,
  Button,
  ButtonGroup,
  Callout,
  Alert,
  Intent,
  InputGroup,
} from "@blueprintjs/core";
import { css } from "@emotion/core";
import { FolderUserMultiSelect } from "./FolderUserMultiSelect";
import { IPermissionType } from "../../../api/admin";
import { PERMISSION_LEVEL_VIEW } from "../../../constants";
import { useItemsById } from "../../../hooks/useSelectedItems";

interface UsersPanelProps {
  selectedUserIds: number[];
  users: any[];
  permissionTypes: IPermissionType[];
  userPermissions: Map<number, number>;
  onUserChange: (userIds: number[]) => void;
  onUserRemove: (userId: number) => Promise<void>;
  onSwitchToGroups?: () => void;
  handleUserPermissionChange: (userId: number, accessLevel: number) => void;
  isFybrAdmin?: boolean;
  isRemoving?: boolean;
  userAction?: React.ReactNode;
}

export function UsersPanel(props: UsersPanelProps) {
  const {
    selectedUserIds,
    users,
    permissionTypes,
    userPermissions,
    onUserChange,
    onUserRemove,
    onSwitchToGroups,
    handleUserPermissionChange,
    isFybrAdmin,
    isRemoving = false,
    userAction,
  } = props;

  const usersById = useItemsById(users);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const filterInputRef = React.useRef<HTMLInputElement>(null);
  const [pendingRemoveUserId, setPendingRemoveUserId] = React.useState<
    number | null
  >(null);
  const [filterQuery, setFilterQuery] = React.useState<string>("");

  React.useEffect(() => {
    const timer = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(timer);
  }, [selectedUserIds.length]);

  const handleRemoveClick = (userId: number) => {
    setPendingRemoveUserId(userId);
  };

  const handleConfirmRemove = async () => {
    if (pendingRemoveUserId !== null) {
      await onUserRemove(pendingRemoveUserId);
      // Only close the alert after removal completes (success or error)
      setPendingRemoveUserId(null);
    }
  };

  const handleCancelRemove = () => {
    setPendingRemoveUserId(null);
  };

  const pendingUser =
    pendingRemoveUserId !== null
      ? usersById.get(pendingRemoveUserId)
      : null;
  const pendingUserName =
    pendingUser?.name ||
    (pendingRemoveUserId !== null ? `User ${pendingRemoveUserId}` : "");

  const filteredUserIds = React.useMemo(() => {
    if (!filterQuery.trim()) return selectedUserIds;
    const query = filterQuery.toLowerCase();
    return selectedUserIds.filter((userId) => {
      const user = usersById.get(userId);
      const userName = user?.name || `User ${userId}`;
      return userName.toLowerCase().includes(query);
    });
  }, [selectedUserIds, usersById, filterQuery]);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 1rem;
        height: 50vh;
      `}
    >
      <div
        css={css`
          padding: 0 1rem;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 0.5rem;
          `}
        >
          <div
            css={css`
              flex: 1;
              min-width: 0;
            `}
          >
            <FolderUserMultiSelect
              selectedUserIds={selectedUserIds}
              users={users}
              onUserChange={onUserChange}
              inputRef={inputRef}
            />
          </div>
          {userAction && (
            <div
              css={css`
                flex-shrink: 0;
              `}
            >
              {userAction}
            </div>
          )}
        </div>
        {selectedUserIds.length > 0 && (
          <div
            css={css`
              margin-top: 0.5rem;
            `}
          >
            <InputGroup
              inputRef={filterInputRef}
              placeholder="Filter Assigned Users..."
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              leftIcon="search"
            />
          </div>
        )}
      </div>
      <div
        css={css`
          background-color: var(--neutral-8);
          border-radius: 4px;
          border-top: 1px solid var(--neutral-5);
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
        `}
      >
        {selectedUserIds.length === 0 ? (
          <div
            css={css`
              padding: 1rem;
              color: var(--neutral-4);
              height: 100%;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              gap: 0.5rem;
            `}
          >
            <div
              css={css`
                width: 100%;
                max-width: 48ch;
              `}
            >
              {isFybrAdmin ? (
                <Callout intent="primary" title="No users selected">
                  <p
                    css={css`
                      margin-bottom: 1rem;
                    `}
                  >
                    Select a user from above to add it to this folder or, add a
                    group by switching to the Groups tab.
                  </p>
                  <ButtonGroup>
                    <Button
                      icon="user"
                      intent="primary"
                      onClick={() => inputRef.current?.focus()}
                    >
                      Add User
                    </Button>
                    <Button
                      icon="people"
                      intent="primary"
                      onClick={onSwitchToGroups}
                    >
                      Add Group
                    </Button>
                  </ButtonGroup>
                </Callout>
              ) : (
                <Callout intent="primary" title="No users selected">
                  <p
                    css={css`
                      margin-bottom: 1rem;
                    `}
                  >
                    Select a user from above to add it to this folder.
                  </p>
                  <Button
                    icon="user"
                    intent="primary"
                    onClick={() => inputRef.current?.focus()}
                  >
                    Add User
                  </Button>
                </Callout>
              )}
            </div>
          </div>
        ) : (
          <>
            <div>
              <HTMLTable
                striped
                interactive
                css={css`
                  width: 100%;
                `}
              >
                <colgroup>
                  <col />
                  <col width="200px" />
                  <col width="62px" />
                </colgroup>
                <thead
                  css={css`
                    box-shadow: inset 0 -1px rgba(255, 255, 255, 0.2);
                  `}
                >
                  <tr>
                    <th>
                      <div
                        css={css`
                          padding: 0.25rem 5px;
                        `}
                      >
                        User Name
                      </div>
                    </th>
                    <th>
                      <div
                        css={css`
                          padding: 0.25rem;
                        `}
                      >
                        Permission
                      </div>
                    </th>
                    <th></th>
                  </tr>
                </thead>
              </HTMLTable>
            </div>
            <div
              css={css`
                overflow: auto;
                flex: 1;

                & table.bp6-html-table tbody tr:first-of-type td {
                  box-shadow: none;
                }
              `}
            >
              <HTMLTable
                striped
                interactive
                css={css`
                  width: 100%;
                `}
              >
                <colgroup>
                  <col />
                  <col width="200px" />
                  <col width="42px" />
                </colgroup>
                <tbody>
                  {filteredUserIds.length === 0 &&
                  filterQuery.trim().length > 0 ? (
                    <tr>
                      <td colSpan={3}>
                        <div
                          css={css`
                            padding: 2rem 1rem;
                            color: var(--neutral-4);
                            text-align: center;
                          `}
                        >
                          No users match your filter.
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredUserIds.map((userId) => {
                      const user = usersById.get(userId);
                      const currentAccessLevel =
                        userPermissions.get(userId) ||
                        permissionTypes.find(
                          (pt) => pt.level === PERMISSION_LEVEL_VIEW
                        )?.level ||
                        PERMISSION_LEVEL_VIEW;
                      return (
                        <tr key={userId}>
                          <td>
                            <div
                              css={css`
                                font-size: 0.875rem;
                                padding: 0.25rem 5px;
                              `}
                            >
                              {user?.name || `User ${userId}`}
                            </div>
                          </td>
                          <td>
                            <HTMLSelect
                              css={css`
                                width: 100%;
                              `}
                              value={currentAccessLevel}
                              onChange={(e) =>
                                handleUserPermissionChange(
                                  userId,
                                  Number(e.target.value)
                                )
                              }
                            >
                              {permissionTypes.map((pt) => (
                                <option key={pt.id} value={pt.level}>
                                  {pt.label}
                                </option>
                              ))}
                            </HTMLSelect>
                          </td>
                          <td>
                            <div
                              css={css`
                                display: flex;
                                justify-content: center;
                                align-items: center;
                                padding: 0 5px;
                              `}
                            >
                              <Button
                                icon="cross"
                                intent="danger"
                                onClick={() => handleRemoveClick(userId)}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </HTMLTable>
            </div>
          </>
        )}
      </div>
      <Alert
        className="bp6-dark"
        isOpen={pendingRemoveUserId !== null}
        icon="warning-sign"
        intent={Intent.DANGER}
        cancelButtonText="Cancel"
        confirmButtonText={isRemoving ? "Removing..." : "Remove"}
        onCancel={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        canEscapeKeyClose={!isRemoving}
        canOutsideClickClose={!isRemoving}
      >
        <p>
          Are you sure you want to remove <strong>{pendingUserName}</strong>?
          This will remove their access to this folder.
        </p>
      </Alert>
    </div>
  );
}
